import DOMPurify from 'dompurify';
import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';

import { Close as CloseIcon } from '@mui/icons-material';
import { Dialog, DialogContent, DialogContentText, DialogTitle, SvgIcon } from '@mui/material';

import { ReactComponent as TimeOutEllipseLowIcon } from 'img/circle_green.svg';
import { ReactComponent as TimeOutEllipseHighIcon } from 'img/circle_red.svg';
import { ReactComponent as TimeOutEllipseMediumIcon } from 'img/circle_yellow.svg';
import { ReactComponent as TimeOutIcon } from 'img/timeout.svg';

import './lockout_timeout_dialog.scss';
interface Props {
    t: Function;
    onClose?: Function;
    timerClose?: Function;
    isClearTimer?: boolean;
}

enum lockOutTimer {
    min = 10,
    sec = 0,
}
let timer: ReturnType<typeof setTimeout> | null = null;

const LockOutTimeOutDialog: FC<Props> = ({ t, onClose, timerClose, isClearTimer }): ReactElement => {
    const navigate = useNavigate();
    const [state, setState] = useImmer({
        timerCountDown: {
            min: 0,
            sec: 0,
        },
    });

    const twoDigits = (n: number): string => {
        return n <= 9 ? `0${n}` : String(n);
    };

    const updateTimer = (endTime: number): void => {
        const msLeft = endTime - +new Date();
        if (msLeft > 1000) {
            const time = new Date(msLeft);
            const mins = time.getUTCMinutes();
            const secs = time.getUTCSeconds();
            setState((draft) => {
                draft.timerCountDown = {
                    min: mins,
                    sec: secs,
                };
            });
            timer = setTimeout(() => updateTimer(endTime), time.getUTCMilliseconds() + 500);
        } else {
            !isClearTimer && timerClose?.();
            if (isClearTimer) {
                clearTimeout(timer as ReturnType<typeof setTimeout>);
                navigate('/securityQuestions/');
            }
            onClose?.(true);
        }
    };

    const setTimer = (minutes: number, seconds: number): void => {
        const endTime = +new Date() + 1000 * (60 * minutes + seconds) + 500;
        updateTimer(endTime);
    };

    useEffect(() => {
        setTimer(lockOutTimer.min, lockOutTimer.sec);
    }, []);

    return (
        <Dialog
            open={true}
            onClose={(_, reason: 'backdropClick' | 'escapeKeyDown'): boolean | void => {
                if (reason === 'backdropClick') {
                    return false;
                }
            }}
            className="lockout-timeout-dialog"
            aria-labelledby="status-reason-title"
        >
            <div className="lockout-timeout">
                <div className="lockout-timeout-c-icon">
                    <CloseIcon
                        className="closePane"
                        onClick={(): void => {
                            isClearTimer && clearTimeout(timer as ReturnType<typeof setTimeout>);
                            onClose?.(false);
                        }}
                        aria-label={t('template.cancel_button')}
                    />
                </div>
                <div className="lockout-timeout-container">
                    <div className="timeout-icon-container">
                        <div className="timeout-status default">
                            <SvgIcon component={TimeOutIcon} />
                        </div>
                        {state.timerCountDown.min >= 7 && state.timerCountDown.min <= 10 ? (
                            <SvgIcon
                                component={TimeOutEllipseHighIcon}
                                className="session-severity"
                                viewBox="0 0 64 64"
                            />
                        ) : state.timerCountDown.min >= 3 && state.timerCountDown.min <= 6 ? (
                            <SvgIcon
                                component={TimeOutEllipseMediumIcon}
                                className="session-severity"
                                viewBox="0 0 64 64"
                            />
                        ) : (
                            <SvgIcon
                                component={TimeOutEllipseLowIcon}
                                className="session-severity"
                                viewBox="0 0 64 64"
                            />
                        )}
                    </div>
                    <DialogTitle id="session_timeout_title" className="lockout-timeout-title">
                        {t('lockout.title')}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="session_timeout_instructions" className="lockout-timeout-instructions">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        t('lockout.instructions', {
                                            LockOutDuration: `${state.timerCountDown.min}: ${twoDigits(
                                                state.timerCountDown.sec
                                            )}`,
                                        })
                                    ),
                                }}
                            />
                        </DialogContentText>
                    </DialogContent>
                </div>
            </div>
        </Dialog>
    );
};

export default LockOutTimeOutDialog;
